import type { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap } from 'rxjs';

import { toChatMessages } from '@core/tellent-assistant-util';

import { injectTellentAssistantThreadsBaseUrl } from '../tellent-assistant-endpoint.provider';
import { TellentAssistantThreadsHttp } from '../tellent-assistant-threads.http';
import {
  TellentAssistantCurrentThreadActions,
  TellentAssistantCurrentThreadSelectors,
} from './tellent-assistant-current-thread.state';
import { TellentAssistantThreadsActions } from './tellent-assistant-threads.state';

export const clearCurrentThreadOnDelete = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(TellentAssistantThreadsActions.removeOnePessimisticSuccess),
      concatLatestFrom(() => store.select(TellentAssistantCurrentThreadSelectors.data)),
      filter(
        ([action, current]) => current !== null && action.payload.data.id === current.threadId,
      ),
      map(() => TellentAssistantCurrentThreadActions.clear()),
    ),
  { functional: true },
);

export const loadTellentAssistantCurrentThread = createEffect(
  (
    actions$ = inject(Actions),
    http = inject(TellentAssistantThreadsHttp),
    baseUrl = injectTellentAssistantThreadsBaseUrl({ optional: true }) ?? '',
  ) =>
    actions$.pipe(
      ofType(TellentAssistantCurrentThreadActions.fetch),
      switchMap(({ payload: { threadId } }) => {
        if (!baseUrl) {
          return of(
            TellentAssistantCurrentThreadActions.fetchSuccess({
              payload: { threadId, messages: [] },
            }),
          );
        }
        return http.load(baseUrl, threadId).pipe(
          map(res =>
            TellentAssistantCurrentThreadActions.fetchSuccess({
              payload: { threadId, messages: toChatMessages(res.messages) },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(TellentAssistantCurrentThreadActions.fetchFailed({ payload: error })),
          ),
        );
      }),
    ),
  { functional: true },
);
